import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const AIRecommendations = ({ onSelectProgram }) => {
  const recommendations = [
  {
    id: 'ai-rec-1',
    name: 'Recovery-Focused Upper Body',
    reason: 'Based on your recent lower body intensity',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d9390292-1764649132607.png",
    imageAlt: 'Athletic man performing dumbbell shoulder press exercise in modern gym with focused expression',
    difficulty: 'Intermediate',
    duration: '45 min',
    matchScore: 95,
    benefits: ['Muscle Recovery', 'Upper Body Strength', 'Injury Prevention']
  },
  {
    id: 'ai-rec-2',
    name: 'Adaptive HIIT Cardio',
    reason: 'Optimized for your current fitness level',
    image: "https://images.unsplash.com/photo-1635863782730-4393246eee1e",
    imageAlt: 'Woman in athletic wear performing high-intensity interval training with battle ropes in fitness studio',
    difficulty: 'Advanced',
    duration: '30 min',
    matchScore: 88,
    benefits: ['Cardiovascular Health', 'Fat Burning', 'Endurance']
  },
  {
    id: 'ai-rec-3',
    name: 'Mobility & Flexibility',
    reason: 'Recommended for injury prevention',
    image: "https://images.unsplash.com/photo-1680543251936-94587c6008d3",
    imageAlt: 'Person performing yoga stretching pose on mat in peaceful studio with natural lighting',
    difficulty: 'Beginner',
    duration: '25 min',
    matchScore: 82,
    benefits: ['Flexibility', 'Joint Health', 'Stress Relief']
  }];


  const insights = [
  {
    title: 'Optimal Training Time',
    description: 'Your performance peaks between 5-7 PM based on workout history',
    icon: 'Clock',
    color: 'primary'
  },
  {
    title: 'Recovery Status',
    description: 'Fully recovered from last workout. Ready for high intensity',
    icon: 'Activity',
    color: 'success'
  },
  {
    title: 'Nutrition Reminder',
    description: 'Consider protein intake within 30 minutes post-workout',
    icon: 'Apple',
    color: 'accent'
  }];


  const getDifficultyColor = (level) => {
    const colors = {
      beginner: 'bg-success/10 text-success',
      intermediate: 'bg-warning/10 text-warning',
      advanced: 'bg-error/10 text-error'
    };
    return colors?.[level?.toLowerCase()] || colors?.beginner;
  };

  const getColorClass = (color) => {
    const colors = {
      primary: 'bg-primary/10 text-primary',
      success: 'bg-success/10 text-success',
      accent: 'bg-accent/10 text-accent'
    };
    return colors?.[color] || colors?.primary;
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="card bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Icon name="Sparkles" size={24} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
              AI-Powered Recommendations
            </h3>
            <p className="text-muted-foreground">
              Personalized workout suggestions based on your fitness level, goals, recent activity, and recovery status. Our AI analyzes your performance patterns to optimize your training.
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {insights?.map((insight, index) =>
        <div key={index} className="card">
            <div className={`w-12 h-12 rounded-lg ${getColorClass(insight?.color)} flex items-center justify-center mb-4`}>
              <Icon name={insight?.icon} size={24} />
            </div>
            <h4 className="font-semibold text-foreground mb-2">{insight?.title}</h4>
            <p className="text-sm text-muted-foreground">{insight?.description}</p>
          </div>
        )}
      </div>
      <div>
        <h3 className="text-2xl font-heading font-semibold text-foreground mb-6">
          Recommended Programs
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {recommendations?.map((program) =>
          <div key={program?.id} className="card hover:shadow-xl transition-all duration-250">
              <div className="relative h-48 md:h-56 -mx-9 -mt-9 mb-6 overflow-hidden rounded-t-xl">
                <Image
                src={program?.image}
                alt={program?.imageAlt}
                className="w-full h-full object-cover" />

                <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2">
                  <Icon name="Sparkles" size={14} />
                  {program?.matchScore}% Match
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4 className="text-lg font-heading font-semibold text-foreground">
                      {program?.name}
                    </h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getDifficultyColor(program?.difficulty)}`}>
                      {program?.difficulty}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <Icon name="Lightbulb" size={14} color="var(--color-accent)" />
                    <span>{program?.reason}</span>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm text-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={16} color="var(--color-primary)" />
                    <span>{program?.duration}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Key Benefits:</p>
                  <div className="flex flex-wrap gap-2">
                    {program?.benefits?.map((benefit, idx) =>
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full bg-muted text-xs text-foreground">

                        {benefit}
                      </span>
                  )}
                  </div>
                </div>

                <Button
                variant="default"
                fullWidth
                iconName="Play"
                iconPosition="left"
                onClick={() => onSelectProgram(program)}>

                  Start Program
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="card bg-muted/50">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
            <Icon name="Info" size={20} color="var(--color-accent)" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-foreground mb-2">
              How AI Recommendations Work
            </h4>
            <p className="text-sm text-muted-foreground">
              Our AI analyzes multiple factors including your workout history, performance trends, recovery patterns, injury history, fitness goals, and current energy levels. The system continuously learns from your feedback and adjusts recommendations to optimize your training results while minimizing injury risk.
            </p>
          </div>
        </div>
      </div>
    </div>);

};

export default AIRecommendations;